import React, { useEffect, useState } from "react";
import {
    Button,
    Grid,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Breadcrumbs,
    Link,
    Chip,
    Box,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { Home as HomeIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";

import UsersTableComponent from "../../../components/admin/users-manager/UsersTable";
import { UsersAddDrawer } from "../../../components/admin/users-manager/UsersAddDrawer";

import { useGetUsersListQuery } from "../../../store/users/usersApis";
import { useGetRegionsDropdownQuery } from "../../../store/masters/mastersApis";
import { defaultPaginate } from "../../../helpers/constants";
import { AddOutlined } from "@mui/icons-material";
import { SearchRecords } from "../../../components/Common/SearchRecords";

const UsersContainer = (props) => {
    const { showDrawer, formType, initialValues, pagination } = props;
    const [userType, setUserType] = useState(null);
    const [userStatus, setUserStatus] = useState(undefined);
    const [isB2B, setIsB2B] = useState(undefined);
    const [selectedRegion, setSelectedRegion] = useState(null);

    // Hierarchy navigation state
    const [currentLevel, setCurrentLevel] = useState("all"); // "all", "distributor", "staff"
    const [parentUser, setParentUser] = useState(null);
    const [hierarchyPath, setHierarchyPath] = useState([]);
    // Determine query parameters based on current hierarchy level
    const getQueryParams = () => {
        const baseParams = {
            ...pagination,
            is_active: userStatus || undefined,
            is_b2b: isB2B !== undefined ? isB2B : undefined,
            region_id: selectedRegion || undefined,
        };

        if (currentLevel === "all") {
            return {
                ...baseParams,
                role: userType || "",
            };
        } else if (currentLevel === "distributor" && parentUser) {
            return {
                ...baseParams,
                role: "distributor",
                parent_id: parentUser.id,
            };
        } else if (currentLevel === "staff" && parentUser) {
            return {
                ...baseParams,
                role: "staff",
                parent_id: parentUser.id,
            };
        }

        return baseParams;
    };

    const { isLoading, data: users, isFetching } = useGetUsersListQuery(getQueryParams());
    const { data: regionsData } = useGetRegionsDropdownQuery();

    const handleAddUser = () => {
        props.changeUserInitialState({
            showDrawer: true,
            formType: "Add",
            initialValues: null,
        });
    };

    useEffect(() => {
        props.handlePaginationState(defaultPaginate);
    }, []);

    const handlePagination = (setting) => {
        props.handlePaginationState(setting);
    };

    const handleDrawerClose = () => {
        props.changeUserInitialState({ showDrawer: false, formType: "", initialValues: null });
    };

    const handleUserEdit = (data, type) => {
        let payload = {
            id: data.id,
            name: data.name,
            email: data.email,
            mobile: data.mobile,
            role: data.role,
            dob: data.dob,
            full_address: data.full_address,
            aadhar_number: data.aadhar_number,
            is_active: data.is_active,
            is_b2b: data.is_b2b,
            parent_id: data.parent_id,
            region_id: data.region_id,
        };
        props.changeUserInitialState({ showDrawer: true, formType: type, initialValues: payload });
    };

    // Handle viewing subordinates
    const handleViewSubordinates = (user) => {
        const newPath = [...hierarchyPath, user];
        setHierarchyPath(newPath);
        setParentUser(user);

        if (user.role === "distributor") {
            setCurrentLevel("staff");
        }
    };

    // Handle breadcrumb navigation
    const handleBreadcrumbClick = (index) => {
        if (index === -1) {
            // Go to root
            setCurrentLevel("all");
            setParentUser(null);
            setHierarchyPath([]);
        } else {
            // Go to specific level
            const newPath = hierarchyPath.slice(0, index + 1);
            setHierarchyPath(newPath);

            if (index === hierarchyPath.length - 1) {
                // Stay at current level
                return;
            } else if (index === 0) {
                // Go to distributor level
                setCurrentLevel("distributor");
                setParentUser(null);
            } else {
                // Go to distributor level
                setCurrentLevel("distributor");
                setParentUser(newPath[0]);
            }
        }
    };

    // Get current view title
    const getCurrentViewTitle = () => {
        if (currentLevel === "all") {
            return "All Users";
        } else if (currentLevel === "distributor") {
            return `Distributors under ${parentUser?.name}`;
        } else if (currentLevel === "staff") {
            return `Staff under ${parentUser?.name}`;
        }
        return "Users";
    };

    // Get current view description
    const getCurrentViewDescription = () => {
        if (currentLevel === "all") {
            return "Viewing all users in the system";
        } else if (currentLevel === "distributor") {
            return `Viewing distributors that report to ${parentUser?.name}`;
        } else if (currentLevel === "staff") {
            return `Viewing staff members that report to ${parentUser?.name}`;
        }
        return "";
    };

    return (
        <Stack spacing={2}>
            {/* Breadcrumb Navigation */}
            {hierarchyPath.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleBreadcrumbClick(-1)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "#1976d2",
                                textDecoration: "none",
                                "&:hover": { textDecoration: "underline" },
                            }}
                        >
                            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                            All Users
                        </Link>
                        {hierarchyPath.map((user, index) => (
                            <Link
                                key={user.id}
                                component="button"
                                variant="body2"
                                onClick={() => handleBreadcrumbClick(index)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: index === hierarchyPath.length - 1 ? "text.primary" : "#1976d2",
                                    textDecoration: "none",
                                    "&:hover": { textDecoration: "underline" },
                                }}
                            >
                                {user.name}
                                {index < hierarchyPath.length - 1 && (
                                    <Chip
                                        label={user.role}
                                        size="small"
                                        sx={{
                                            ml: 1,
                                            height: 20,
                                            fontSize: "0.7rem",
                                            backgroundColor: user.role === "distributor" ? "#fff3e0" : "#f3e5f5",
                                            color: user.role === "distributor" ? "#f57c00" : "#7b1fa2",
                                        }}
                                    />
                                )}
                            </Link>
                        ))}
                    </Breadcrumbs>
                </Box>
            )}

            <Grid container justifyContent="space-between">
                <Grid item xs={6} sx={{ alignSelf: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {/* Back Button - Only show when not at root level */}
                        {currentLevel !== "all" && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => {
                                    if (hierarchyPath.length === 1) {
                                        // Go back to distributor level
                                        setCurrentLevel("distributor");
                                        setParentUser(null);
                                        setHierarchyPath([]);
                                    } else if (hierarchyPath.length > 1) {
                                        // Go back to distributor level
                                        const newPath = hierarchyPath.slice(0, -1);
                                        setHierarchyPath(newPath);
                                        setParentUser(newPath[newPath.length - 1]);
                                        setCurrentLevel("distributor");
                                    } else {
                                        // Go back to all users
                                        setCurrentLevel("all");
                                        setParentUser(null);
                                        setHierarchyPath([]);
                                    }
                                }}
                                sx={{
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontSize: "0.875rem",
                                    minWidth: "auto",
                                    px: 2,
                                }}
                            >
                                Back
                            </Button>
                        )}
                        <Box>
                            <Typography variant="h6">
                                {getCurrentViewTitle()}{" "}
                                <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super" }}>
                                    ({users?.count || 0})
                                </span>{" "}
                            </Typography>
                            {getCurrentViewDescription() && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {getCurrentViewDescription()}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{ alignSelf: "center" }}
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"flex-end"}
                >
                    <SearchRecords handlePagination={handlePagination} pagination={pagination} />
                    <Button
                        disableElevation
                        variant="contained"
                        sx={{
                            borderRadius: "8px",
                            marginLeft: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: "16px",
                        }}
                        onClick={() => handleAddUser()}
                    >
                        <AddOutlined />
                        New User
                    </Button>
                </Grid>
            </Grid>

            {/* Filters - Only show when not in hierarchy view */}
            {currentLevel === "all" && (
                <Grid container gap={10} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                    <Grid item display={"flex"} flexDirection={"row"} gap={2}>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1} alignItems={"center"}>
                            <Typography variant="subtitle2" color="textPrimary">
                                User Type{" "}
                            </Typography>
                            <ToggleButtonGroup
                                color="primary"
                                value={userType}
                                exclusive
                                onChange={(e, val) => {
                                    setUserType(val ? val : "");
                                    // Reset B2B filter when role changes
                                    setIsB2B(undefined);
                                }}
                                aria-label="image"
                                size="small"
                            >
                                <ToggleButton value="admin" aria-label="" sx={{ px: 2 }}>
                                    Admin
                                </ToggleButton>
                                <ToggleButton value="distributor" aria-label="" sx={{ px: 2 }}>
                                    Distributor
                                </ToggleButton>
                                <ToggleButton value="staff" aria-label="" sx={{ px: 2 }}>
                                    Staff
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1} alignItems={"center"}>
                            <Typography variant="subtitle2" color="textPrimary">
                                Status{" "}
                            </Typography>
                            <ToggleButtonGroup
                                color="primary"
                                value={userStatus}
                                exclusive
                                onChange={(e, val) => setUserStatus(val ? val : undefined)}
                                aria-label="image"
                                size="small"
                            >
                                <ToggleButton value={true} aria-label="" sx={{ px: 2 }}>
                                    Active
                                </ToggleButton>
                                <ToggleButton value="false" aria-label="" sx={{ px: 2 }}>
                                    Inactive
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                        {/* Show B2B filter only when staff role is selected */}
                        {userType === "staff" && (
                            <Grid item display={"flex"} flexDirection={"row"} gap={1} alignItems={"center"}>
                                <Typography variant="subtitle2" color="textPrimary">
                                    B2B Status{" "}
                                </Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={isB2B}
                                    exclusive
                                    onChange={(e, val) => setIsB2B(val !== undefined ? val : undefined)}
                                    aria-label="b2b"
                                    size="small"
                                >
                                    <ToggleButton value={true} aria-label="" sx={{ px: 2 }}>
                                        B2B
                                    </ToggleButton>
                                    <ToggleButton value={false} aria-label="" sx={{ px: 2 }}>
                                        Regular
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                        )}
                        {/* Region Filter */}
                        <Grid item display={"flex"} flexDirection={"row"} gap={1} alignItems={"center"}>
                            <Typography variant="subtitle2" color="textPrimary">
                                Region{" "}
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                    value={selectedRegion || ""}
                                    onChange={(e) => setSelectedRegion(e.target.value || null)}
                                    displayEmpty
                                >
                                    <MenuItem value="">
                                        <em>All Regions</em>
                                    </MenuItem>
                                    {regionsData?.map((region) => (
                                        <MenuItem key={region.value} value={region.value}>
                                            {region.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {/* Hierarchy Info Alert */}
            {currentLevel !== "all" && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Hierarchy View:</strong> {getCurrentViewDescription()}
                        {currentLevel === "staff" && <span> • Staff members cannot have subordinates</span>}
                    </Typography>
                </Alert>
            )}

            <Grid container>
                <Grid item xs={12}>
                    <UsersTableComponent
                        edit={(val, type) => handleUserEdit(val, type)}
                        loading={isLoading}
                        fetching={isFetching}
                        count={users?.count}
                        data={users?.results || []}
                        pagination={pagination}
                        handlePagination={(val) => handlePagination(val)}
                        onViewSubordinates={handleViewSubordinates}
                        currentLevel={currentLevel}
                        parentUser={parentUser}
                    />
                </Grid>
            </Grid>
            <UsersAddDrawer
                show={showDrawer}
                close={handleDrawerClose}
                formType={formType}
                initialValues={initialValues}
            />
        </Stack>
    );
};

export default UsersContainer;
